import {
  addDownloadButtonToChart,
  displayNoDataOverlay,
  getChartContainerDimensions,
} from './charts_utils.js';

export function calculate_svg_space(element = window) {
  const aspect = 1260 / 700;
  // const width = Math.min(
  //   $(element).innerWidth() * 1,
  //   $(element).innerHeight() * 0.55 * aspect
  // );
  height = window.innerHeight * 0.55;
  width = height * aspect;

  // const height = width / aspect;
  return [width, height];
}

const SPHERE = { type: "Sphere" };

// The globe's outline, drawn under the countries so the curved edge of the
// projection reads as the map's boundary and zooms with the land.
function appendSphere(group, path) {
  group
    .append("path")
    .attr("class", "sphere")
    .datum(SPHERE)
    .attr("d", path)
    .style("fill", "var(--map-ocean, transparent)")
    .style("stroke", "var(--map-border, black)")
    .style("stroke-width", 0.6);
}

export function createBubbleMap(containerID, dataGeo, data, downloadable = true) {
  const graphContainer = d3.select("#" + containerID);
  graphContainer.selectAll("*").remove();
  graphContainer.innerHTML = "";
  graphContainer
    .style("max-width", null)
    .style("width", null)
    .style("display", null)
    .style("position", null)
    .style("overflow", "hidden");

  // Statically imported rather than probed with `typeof`: the guard was always
  // false because the name was never imported, so both maps were laid out for a
  // fixed 960x500 whatever size the panel actually was.
  const chartSize = getChartContainerDimensions(containerID, 960, 500);
  var width = chartSize.width;
  var height = chartSize.height;

  // Now, append the SVG element
  const svg = graphContainer
    .append("svg")
    .attr("id", "my_dataviz")
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("width", width)
    .attr("height", height)
    .style("width", "100%")
    .style("height", `${height}px`)
    .style("border", "0");

  const maxScaleFactor = 15; // maximum magnification factor

  // Equal Earth, an equal-area pseudocylindrical projection, replaces the old
  // Patterson cylinder, which stretched the high latitudes into a flat
  // rectangle. Fitting to the sphere rather than to the land features keeps
  // the curved outline fully in frame whatever the panel's aspect ratio.
  const projection = d3.geoEqualEarth();
  const path = d3.geoPath().projection(projection);

  svg
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("width", width)
    .attr("height", height);
  projection.fitSize([width, height], SPHERE);

  // Coordinate provenance, three fixed slots keyed by gps_category (1..3).
  // The steps live in tokens.css so each theme gets its own, which is what the
  // old three-step Blues ramp could not do: its darkest step vanished into the
  // dark basemap.
  // Order follows the database's Compound_Attribute mapping for GPS Category:
  // 1 = Exact location, 2 = Country center, 3 = Region/Place. The legend used to
  // list 2 as Region/Place and 3 as Country center, which mislabeled every bubble
  // in those two categories.
  const COORD_SLOTS = [
    "var(--map-coord-exact, #15598c)",
    "var(--map-coord-country, #5ba9de)",
    "var(--map-coord-region, #e08a2e)",
  ];
  const color = d3.scaleOrdinal().domain([1, 2, 3]).range(COORD_SLOTS);

  // Filter out any data points with invalid coordinates or counts
  data = data.filter((d) => {
    const lat = +d.Lat,
      lng = +d.Long,
      cnt = +d.Count;
    return isFinite(lat) && isFinite(lng) && isFinite(cnt) && cnt > 0;
  });

  if (!data.length) {
    displayNoDataOverlay(containerID, "No valid location data available");
    return;
  }

  const valueExtent = d3.extent(data, (d) => +d.Count);
  const size = d3
    .scaleSqrt()
    .domain(valueExtent)
    .range([3, width / 20]);

  const countriesGroup = svg.append("g").attr("class", "countries");
  appendSphere(countriesGroup, path);

  countriesGroup
    .selectAll("path.country")
    .data(dataGeo.features)
    .enter()
    .append("path")
    .attr("class", "country")
    .style("fill", "var(--map-land, #999)")
    .attr("d", path)
    .style("stroke", "var(--map-border, black)")
    .style("stroke-width", 0.4)
    .style("opacity", 1);

  const circlesGroup = svg.append("g").attr("class", "circles");

  const div = d3
    .select("body")
    .append("div")
    .attr("class", "tooltip-mapchart")
    .style("opacity", 0);

  const minRadius = 1;
  const maxRadius = 20;

  circlesGroup
    .selectAll("circle")
    .data(data)
    .enter()
    .append("circle")
    .attr("cx", (d) => {
      const p = projection([+d.Long, +d.Lat]);
      return p ? p[0] : 0;
    })
    .attr("cy", (d) => {
      const p = projection([+d.Long, +d.Lat]);
      return p ? p[1] : 0;
    })
    .attr("r", (d) => Math.max(minRadius, Math.min(size(+d.Count), maxRadius)))
    .style("fill", (d) => color(d.Color))
    .attr("stroke-width", 0.9)
    .attr("fill-opacity", 0.6)
    .attr("stroke", (d) => color(d.Color))
    .on("mouseover", function (event, d) {
      d3.select(this)
        .attr("stroke", "var(--color-text-strong, #16384e)")
        .attr("stroke-width", 2);
      div.transition().duration(200).style("opacity", 0.9);

      div
        .html(
          "Latitude: " +
          d.Lat +
          "<br/>" +
          "Longitude: " +
          d.Long +
          "<br/>" +
          "Count: " +
          d.Count,
        )
        .style("left", event.pageX + 10 + "px")
        .style("top", event.pageY - 28 + "px");
    })
    .on("mouseout", function (event, d) {
      d3.select(this)
        .attr("stroke", (d) => color(d.Color))
        .attr("stroke-width", 0.9);

      div.transition().duration(500).style("opacity", 0);
    });

  // Add legend
  const legendGroup = svg
    .append("g")
    .attr("class", "legend")
    .attr("transform", `translate(${width / 24}, ${(height * 3) / 4})`);

  const labels = ["Exact location", "Country center", "Region/Place"];
  // Genomes per provenance group, so the legend doubles as a tally the way the
  // pie legend does. Summing Count (not counting points) matches the pie: a
  // location with 12 genomes is 12, not 1.
  const countByCategory = d3.rollup(
    data,
    (v) => d3.sum(v, (d) => +d.Count),
    (d) => +d.Color,
  );
  const fmt = d3.format(",");
  const shapeWidth = 30;
  const shapeHeight = 20;
  const spacing = 4;

  // Title
  legendGroup
    .append("text")
    .attr("class", "legend-title")
    .attr("x", 0)
    .attr("y", -8)
    .style("font-size", "12px")
    .style("font-weight", "bold")
    .text("Coordinate type");

  // Legend items double as filters: clicking one keeps only that provenance
  // group's bubbles on the map, and the "Show all" button under the items
  // brings the other groups back. null means no filter.
  let activeCategory = null;

  const items = legendGroup
    .selectAll(".legend-item")
    .data(labels)
    .join("g")
    .attr("class", "legend-item")
    .attr("transform", (d, i) => `translate(0, ${i * (shapeHeight + spacing)})`)
    .style("cursor", "pointer")
    .on("click", (event, d) => {
      const category = labels.indexOf(d) + 1;
      activeCategory = activeCategory === category ? null : category;
      applyCategoryFilter();
    });

  items
    .append("rect")
    .attr("width", shapeWidth)
    .attr("height", shapeHeight)
    // gps_category is 1-based, so slot i of the label list is category i + 1.
    // Passing the 0-based index made d3.scaleOrdinal extend its domain with 0
    // and wrap the range, which shifted every swatch one category down and left
    // the first two labels sharing a colour.
    .attr("fill", (d, i) => color(i + 1))
    .attr("rx", 3);

  items
    .append("text")
    .attr("x", shapeWidth + 6)
    .attr("y", shapeHeight / 2)
    .attr("dy", "0.35em")
    .style("font-size", "12px")
    .text((d, i) => `${d} (${fmt(countByCategory.get(i + 1) || 0)})`);

  const resetItem = legendGroup
    .append("g")
    .attr("class", "legend-reset")
    .attr("transform", `translate(0, ${labels.length * (shapeHeight + spacing) + 8})`)
    .style("cursor", "pointer")
    .on("click", () => {
      activeCategory = null;
      applyCategoryFilter();
    });

  resetItem
    .append("rect")
    .attr("width", 76)
    .attr("height", shapeHeight)
    .attr("rx", shapeHeight / 2)
    .attr("fill", "var(--color-surface, #fff)")
    .attr("stroke", "var(--color-border-strong, #b7c9da)");

  resetItem
    .append("text")
    .attr("x", 38)
    .attr("y", shapeHeight / 2)
    .attr("dy", "0.35em")
    .attr("text-anchor", "middle")
    .style("font-size", "12px")
    .text("Show all");

  function applyCategoryFilter() {
    circlesGroup
      .selectAll("circle")
      .style("display", (d) =>
        activeCategory === null || +d.Color === activeCategory ? null : "none",
      );
    items.attr("opacity", (d, i) =>
      activeCategory === null || activeCategory === i + 1 ? 1 : 0.35,
    );
    // Reads as disabled while every group is already showing.
    resetItem.attr("opacity", activeCategory === null ? 0.45 : 1);
  }

  applyCategoryFilter();

  const zoom = d3
    .zoom()
    .scaleExtent([1, maxScaleFactor])
    .translateExtent([
      [0, 0],
      [width, height],
    ])
    .on("zoom", zoomed);

  svg.call(zoom);

  function zoomed(event) {
    countriesGroup.attr("transform", event.transform);
    circlesGroup.attr("transform", event.transform);

    circlesGroup
      .selectAll("circle")
      .attr("r", (d) =>
        Math.max(
          minRadius,
          Math.min(size(+d.Count) / event.transform.k, maxRadius),
        ),
      );
  }
  if (typeof addDownloadButtonToChart === "function" && downloadable)
    addDownloadButtonToChart("bubblemap", containerID);
}

/**
 * The colour ramps the heatmap can be drawn with. Every one is a continuous gradient
 * from d3-scale-chromatic; "Blues" is the ramp the map has always used. Looked up by
 * name at draw time rather than held as functions, so a ramp missing from the bundle
 * falls back to Blues instead of throwing.
 */
export const HEAT_SCHEMES = Object.freeze([
  { id: "blues", label: "Blues", ramp: "interpolateBlues" },
  { id: "greens", label: "Greens", ramp: "interpolateGreens" },
  { id: "oranges", label: "Oranges", ramp: "interpolateOranges" },
  { id: "purples", label: "Purples", ramp: "interpolatePurples" },
  { id: "ylgnbu", label: "Yellow to blue", ramp: "interpolateYlGnBu" },
  { id: "ylorrd", label: "Yellow to red", ramp: "interpolateYlOrRd" },
  { id: "viridis", label: "Viridis", ramp: "interpolateViridis" },
  { id: "plasma", label: "Plasma", ramp: "interpolatePlasma" },
]);
const HEAT_SCHEME_KEY = "heatmapScheme";

export function readHeatScheme() {
  let id = null;
  try {
    id = localStorage.getItem(HEAT_SCHEME_KEY);
  } catch {
    /* no storage: the default ramp */
  }
  return HEAT_SCHEMES.find((s) => s.id === id) || HEAT_SCHEMES[0];
}

export function writeHeatScheme(id) {
  try {
    localStorage.setItem(HEAT_SCHEME_KEY, id);
  } catch {
    /* private mode: the choice lasts for this render only */
  }
}

export function createHeatMap(containerID, dataGeo, data, downloadable = true) {
  // Filter invalid entries
  data = (data || []).filter(
    (d) => d.Country && isFinite(+d.Count) && +d.Count > 0,
  );

  const graphContainer = d3.select("#" + containerID);
  graphContainer.selectAll("*").remove();
  graphContainer.innerHTML = "";
  graphContainer
    .style("max-width", null)
    .style("width", null)
    .style("display", null)
    .style("position", null)
    .style("overflow", "hidden");

  const chartSize = getChartContainerDimensions(containerID, 960, 500);
  var width = chartSize.width;
  var height = chartSize.height;

  let svg = graphContainer
    .append("svg")
    .attr("id", "my_dataviz")
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("width", width)
    .attr("height", height)
    .style("width", "100%")
    .style("height", `${height}px`)
    .style("border", "0");

  svg.selectAll("*").remove();
  d3.selectAll(".tooltip").remove();

  const maxScaleFactor = 15; // maximum magnification factor

  // Equal Earth, an equal-area pseudocylindrical projection, replaces the old
  // Patterson cylinder, which stretched the high latitudes into a flat
  // rectangle. Fitting to the sphere rather than to the land features keeps
  // the curved outline fully in frame whatever the panel's aspect ratio.
  const projection = d3.geoEqualEarth();
  const path = d3.geoPath().projection(projection);

  svg
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("width", width)
    .attr("height", height);
  projection.fitSize([width, height], SPHERE);

  const countriesGroup = svg.append("g").attr("class", "countries");

  // Count unique values in the data
  const uniqueCountValues = [...new Set(data.map((d) => d.Count))];

  const domain = d3.extent(data, (d) => +d.Count);
  // A cube-root scale: one country with a thousand genomes must not wash out the
  // dozens with three or four.
  const scheme = readHeatScheme();
  const ramp = typeof d3[scheme.ramp] === "function" ? d3[scheme.ramp] : d3.interpolateBlues;
  const color = d3
    .scaleSequential(ramp)
    .domain(domain.map((value) => Math.cbrt(value)));

  const div = d3
    .select("body")
    .append("div")
    .attr("class", "tooltip-mapchart")
    .style("opacity", 0);

  appendSphere(countriesGroup, path);

  countriesGroup
    .selectAll("path.country")
    .data(dataGeo.features)
    .enter()
    .append("path")
    .attr("class", "country")
    .attr("d", path)
    .style("fill", (d) => {
      const countryData = data.find(
        (country) => country.Country === d.properties.name,
      );
      return countryData ? color(Math.cbrt(+countryData.Count)) : "var(--map-nodata, #ccc)";
    })
    .style("stroke", "var(--map-border, black)")
    .style("stroke-width", 0.2)
    .style("opacity", 1)
    .on("mouseover", function (event, d) {
      d3.select(this)
        .style("stroke", "var(--color-text-strong, #16384e)")
        .style("stroke-width", 1.2)
        .raise();
      div.transition().duration(200).style("opacity", 0.9);
      const countryData = data.find(
        (country) => country.Country === d.properties.name,
      );
      const count = countryData ? countryData.Count : 0;
      div
        .html("" + d.properties.name + "<br/>" + "" + count)
        .style("left", event.pageX + 10 + "px")
        .style("top", event.pageY - 28 + "px");
    })
    .on("mouseout", function (event, d) {
      d3.select(this)
        .style("fill", (d) => {
          const countryData = data.find(
            (country) => country.Country === d.properties.name,
          );
          return countryData ? color(Math.cbrt(+countryData.Count)) : "var(--map-nodata, #ccc)";
        })
        .style("stroke", "var(--map-border, black)")
        .style("stroke-width", 0.2);

      div.transition().duration(500).style("opacity", 0);
    });

  // The legend is the ramp itself: a vertical gradient bar with the genome counts
  // marked along it, so the reader can place any country's shade on it. The discrete
  // "above N" cells it replaces implied steps the map never had.
  const legendGroup = svg
    .append("g")
    .attr("class", "legendSequential")
    .attr("transform", `translate(${width / 24}, ${height * 0.55})`);

  const barW = Math.max(10, Math.round(width / 100));
  const barH = Math.max(90, Math.round(height * 0.28));
  const [domainMin, domainMax] = domain;
  // Positions along the bar follow the same cube-root scale the colours do, so a tick
  // sits exactly beside the shade it names.
  const lo = Math.cbrt(domainMin);
  const hi = Math.cbrt(domainMax);
  // One country only: a collapsed domain would put every tick at NaN.
  const pos = d3
    .scaleLinear()
    .domain(hi > lo ? [lo, hi] : [lo - 1, hi])
    .range([barH, 0]);

  const gradId = `heat-ramp-${containerID}-${scheme.id}`;
  const grad = svg
    .append("defs")
    .append("linearGradient")
    .attr("id", gradId)
    .attr("x1", "0")
    .attr("x2", "0")
    .attr("y1", "1")
    .attr("y2", "0");
  d3.range(0, 1.0001, 0.1).forEach((t) => {
    grad
      .append("stop")
      .attr("offset", `${Math.round(t * 100)}%`)
      .attr("stop-color", ramp(t));
  });

  legendGroup
    .append("text")
    .attr("class", "legend-title")
    .attr("x", 0)
    .attr("y", -8)
    .style("font-size", "12px")
    .style("font-weight", "bold")
    .text("Number of genomes");

  legendGroup
    .append("rect")
    .attr("class", "legend-ramp")
    .attr("width", barW)
    .attr("height", barH)
    .attr("fill", `url(#${gradId})`)
    .attr("stroke", "var(--map-border, black)")
    .attr("stroke-width", 0.3);

  // Integer ticks across the count range, the ends always included. Candidates come from
  // both scales: linear ticks read as round numbers, and ticks taken evenly along the
  // cube-root bar cover its lower half, where the linear ones never land. The bar is
  // short and the ramp compresses the top, so 1500 and 2000 fell a few pixels apart and
  // their labels printed over each other; anything closer to a kept tick than one line of
  // text is dropped, the two ends first in line so they always survive.
  const roundish = (v) => {
    const n = Math.max(1, Math.round(v));
    if (n < 10) return n;
    const mag = 10 ** Math.floor(Math.log10(n));
    return Math.round(n / mag) * mag;
  };
  const candidates = [...new Set(
    [
      ...d3.ticks(domainMin, domainMax, Math.min(5, uniqueCountValues.length)),
      ...pos.ticks(6).map((c) => c ** 3),
    ]
      .map(roundish)
      .filter((v) => v > domainMin && v < domainMax),
  )].sort((a, b) => a - b);
  const minGap = 13;
  const yOf = (v) => pos(Math.cbrt(v));
  const kept = domainMax > domainMin ? [domainMin, domainMax] : [domainMin];
  candidates.forEach((v) => {
    if (kept.every((k) => Math.abs(yOf(k) - yOf(v)) >= minGap)) kept.push(v);
  });
  const ticks = kept.sort((a, b) => a - b);

  const items = legendGroup
    .selectAll(".legend-item")
    .data(ticks)
    .join("g")
    .attr("class", "legend-item")
    .attr("transform", (d) => `translate(0, ${pos(Math.cbrt(d))})`);

  items
    .append("line")
    .attr("x1", barW)
    .attr("x2", barW + 4)
    .attr("stroke", "var(--color-text, #1f2a33)")
    .attr("stroke-width", 1);

  items
    .append("text")
    .attr("x", barW + 7)
    .attr("dy", "0.35em")
    .style("font-size", "12px")
    .text((d) => d.toLocaleString());

  // The ramp picker. Rebuilt with the map, because the container is emptied on every
  // draw; the choice itself is remembered across maps and visits.
  graphContainer.style("position", "relative");
  const picker = graphContainer
    .append("label")
    .attr("class", "heatmap-scheme");
  picker.append("span").text("Colours");
  const select = picker
    .append("select")
    .attr("class", "form-select form-select-sm")
    .attr("aria-label", "Heatmap colour scheme");
  select
    .selectAll("option")
    .data(HEAT_SCHEMES)
    .join("option")
    .attr("value", (d) => d.id)
    .property("selected", (d) => d.id === scheme.id)
    .text((d) => d.label);
  select.on("change", function () {
    writeHeatScheme(this.value);
    createHeatMap(containerID, dataGeo, data, downloadable);
  });

  const zoom = d3
    .zoom()
    .scaleExtent([1, maxScaleFactor])
    .translateExtent([
      [0, 0],
      [width, height],
    ])
    .on("zoom", zoomed);

  svg.call(zoom);

  function zoomed(event) {
    countriesGroup.attr("transform", event.transform);
  }

  // Add download button
  if (typeof addDownloadButtonToChart === "function" && downloadable)
    addDownloadButtonToChart("heatmap", containerID);
}


export async function loadGeoData(url) {
  return await d3.json(url);
}

export function displayAll(config) {

  let HeatMapData = config.heatMapData;
  let BubbleMapData = config.bubbleMapData;
  let graphContainerID = "graphContainer";

  HeatMapData.forEach((d) => {
    d.Country = d.country;
    d.Count = +d.count;
  });

  BubbleMapData.forEach((d) => {
    const parts = String(d.gps_coordinates || '').split(" ").map(Number);
    d.Lat = parts[0];
    d.Long = parts[1];
    d.Color = +d.gps_category;
    d.Count = +d.count;
  });
  BubbleMapData = BubbleMapData.filter(d => isFinite(d.Lat) && isFinite(d.Long) && d.Count > 0);
  console.log(`Our HeatMapData is ${HeatMapData.length} entries long, and BubbleMapData is ${BubbleMapData.length} entries long.`);

  loadGeoData(config.geoDataUrl)
    .then((dataGeo) => {
      // Successfully loaded geo data
      console.log(`Successfully loaded geo data. size: ${dataGeo.length}, features: ${dataGeo.features.length}`);

      const button = document.getElementById("js-button");
      const toggleWrapper = document.getElementById("map-toggle-wrapper");
      let showingBubbleMap = false;

      // Both maps get the SVG download button here as they do on the result
      // page; the standalone map page used to pass false and hide it.
      function renderCurrentMap() {
        if (showingBubbleMap) {
          createBubbleMap(graphContainerID, dataGeo, BubbleMapData);
        } else {
          createHeatMap(graphContainerID, dataGeo, HeatMapData);
        }
      }

      renderCurrentMap();

      // Handle window resize
      window.addEventListener("resize", () => {
        renderCurrentMap();
      });

      // Handle button click
      if (button) {
        button.addEventListener("click", function (event) {
          showingBubbleMap = !showingBubbleMap;
          toggleWrapper.classList.toggle("turn-on", showingBubbleMap);
          renderCurrentMap();
        });
      }
    })
    .catch((error) => {
      console.error("Error loading the GeoJSON data:", error);
    });
}
